import { useCallback, useEffect, useReducer } from 'react';

import { saveCurrentSong } from '@redux/player';
import { useDispatch } from 'react-redux';

import { MAX_TIMEOUT_DELAY_IN_MILLISECONDS } from 'constants/timeout';
import { getRemainingTimeInMilliseconds } from 'services/helpers/formatTime';
import { postCastingEvent } from 'services/networking/casting';
import { PlayerEvent } from 'shared-types/player-event';

import { useUser } from './useUser';

// State when free trial exists (can either be ongoing or expired)
interface WithFreeTrialState {
  isTrialEndDisplayed: false;
  isTrialOngoingDisplayed: boolean;
  trialInitialRemainingTime: number;
}

interface WithoutFreeTrialState {
  isTrialEndDisplayed: boolean;
  isTrialOngoingDisplayed: false;
  trialInitialRemainingTime: null;
}

type FreeTrialState = WithFreeTrialState | WithoutFreeTrialState;

const initialState: FreeTrialState = {
  isTrialEndDisplayed: false,
  isTrialOngoingDisplayed: false,
  trialInitialRemainingTime: null,
};

export enum FreeTrialActionType {
  endFreeTrial = 0,
  hideFreeTrialEnd = 1,
  displayTrialOngoing = 2,
  hideFreeTrialOngoing = 3,
}
type FreeTrialAction =
  | {
      type: FreeTrialActionType.endFreeTrial;
    }
  | {
      type: FreeTrialActionType.hideFreeTrialEnd;
    }
  | { type: FreeTrialActionType.displayTrialOngoing; payload: number }
  | {
      type: FreeTrialActionType.hideFreeTrialOngoing;
    };

const reducer = (state: FreeTrialState, action: FreeTrialAction): FreeTrialState => {
  switch (action.type) {
    case FreeTrialActionType.endFreeTrial:
      return {
        isTrialEndDisplayed: true,
        isTrialOngoingDisplayed: false,
        trialInitialRemainingTime: null,
      };
    case FreeTrialActionType.hideFreeTrialEnd:
      return { ...state, isTrialEndDisplayed: false };
    case FreeTrialActionType.displayTrialOngoing:
      return {
        isTrialEndDisplayed: false,
        isTrialOngoingDisplayed: true,
        trialInitialRemainingTime: action.payload,
      };
    case FreeTrialActionType.hideFreeTrialOngoing:
      return { ...state, isTrialOngoingDisplayed: false };
  }
};

export const useFreeTrialState = (castingSessionId: string | null) => {
  const [
    { isTrialEndDisplayed, isTrialOngoingDisplayed, trialInitialRemainingTime },
    dispatchReducer,
  ] = useReducer(reducer, initialState);
  const { userInfos } = useUser();
  const dispatchRedux = useDispatch();
  const freeTrialEnd = userInfos?.freeTrialEnd;

  const hideTrialEnd = useCallback(
    () => dispatchReducer({ type: FreeTrialActionType.hideFreeTrialEnd }),
    [],
  );
  const hideTrialOngoing = useCallback(
    () => dispatchReducer({ type: FreeTrialActionType.hideFreeTrialOngoing }),
    [],
  );

  useEffect(() => {
    if (!freeTrialEnd) {
      return;
    }

    const initialRemainingTime = getRemainingTimeInMilliseconds(freeTrialEnd);

    if (initialRemainingTime > 0) {
      dispatchReducer({
        type: FreeTrialActionType.displayTrialOngoing,
        payload: initialRemainingTime,
      });

      if (initialRemainingTime > MAX_TIMEOUT_DELAY_IN_MILLISECONDS) {
        // Do not set the timeout if remaining time is too high because :
        // 1. it is very unlikely that a session stays open for a very long time
        // 2. too large values can break the timeout and act like 0 values
        return;
      }

      const timer = setTimeout(() => {
        dispatchReducer({ type: FreeTrialActionType.endFreeTrial });
        dispatchRedux(saveCurrentSong(null));

        if (castingSessionId) {
          postCastingEvent(castingSessionId, PlayerEvent.SELECT_SONG, '');
        }
      }, initialRemainingTime);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [castingSessionId, dispatchRedux, freeTrialEnd]);

  return {
    isTrialEndDisplayed,
    isTrialOngoingDisplayed,
    hideTrialEnd,
    hideTrialOngoing,
    trialInitialRemainingTime,
  };
};
